import * as React from "react";
import { Suspense, lazy } from "react";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { Header, Footer, HelmetCust } from "../components";
import { ReactNotifications } from "react-notifications-component";
import "./routes.scss";
import { ScrollToTop } from "../utils/commonFunctions/ScrollToTop";
import { BouncingDotsLoader } from "../components/widgets";
import { RoutingURLs } from "../config/RoutingURLs";
import ProtectedRoutes from "./ProtectedRoutes";
import { useAppSelector } from "../app/hooks";
import EnvConfig from "../config/env.config.json";
import { Box, CircularProgress } from "@mui/material";
import { landingPageInfo } from "../utils";
import circularLoaderReach from "../assets/Common/reach_loader.gif";

const LazyLogin = lazy(() => import("../pages/Login"));
const LazyCheckout = lazy(() => import("../pages/Checkout"));
const LazySignUp = lazy(() => import("../pages/SignUp"));
const LazyGiveBack = lazy(() => import("../pages/Giveback"));
const LazyBestFit = lazy(() => import("../pages/BestFit"));
const LazyReviews = lazy(() => import("../pages/Reviews"));
const LazyImeiCompatibilityCheck = lazy(
  () => import("../pages/CompatibilityCheck")
);
const LazyTerms = lazy(() => import("../pages/Terms"));
const LazyWarranty = lazy(() => import("../pages/Warranty"));
const LazyULPromoTerms = lazy(() => import("../pages/ULPromoTerms"));
const LazyPrivacy = lazy(() => import("../pages/Privacy"));
const LazyPlans = lazy(() => import("../pages/Plans"));
const LazyAboutUs = lazy(() => import("../pages/AboutUs"));
const LazyHome = lazy(() => import("../pages/Home"));
const LazyInternationalServices = lazy(
  () => import("../pages/InternationServices")
);
const LazyContactUs = lazy(() => import("../pages/ContactUs"));
const LazyServices = lazy(() => import("../pages/Services"));
const LazyCoverageCheck = lazy(() => import("../pages/CoverageCheck"));
const LazyBillCalculator = lazy(() => import("../pages/BillCalculator"));
const LazyProfile = lazy(() => import("../pages/PostPurchase/Profile"));
const LazyPostHome = lazy(() => import("../pages/PostActivationPages/PAHome"));
const LazyHelpCenter = lazy(
  () => import("../pages/PostActivationPages/HelpCenter")
);
const LazyHelp = lazy(() => import("../pages/Help"));
const LazyLoaderPage = lazy(() => import("../pages/Loader"));
const LazyAccount = lazy(() => import("../pages/PostActivationPages/Account"));
const LazyTrackOrder = lazy(() => import("../pages/TrackOrder"));
const LazyDeviceConfirmCheck = lazy(
  () => import("../pages/PostPurchase/DeviceCheckConfim")
);
const LazyDiscover = lazy(() => import("../pages/Discover"));
const LazyBilling = lazy(() => import("../pages/PostActivationPages/Billing"));
const LazyMyAccount = lazy(() => import("../pages/PostPurchase/MyAccount"));
const LazyActivation = lazy(
  () => import("../pages/PostActivationPages/Activation")
);
const LazySecondaryUserHome = lazy(() => import("../pages/SecondaryUserHome"));
const LazyActivationSteps = lazy(() => import("../pages/ActivationSteps"));
const LazyActivateEsim = lazy(() => import("../pages/ActivateEsim"));
const LazySettings = lazy(() => import("../pages/Settings"));
const LazyWallet = lazy(() => import("../pages/wallet"));
const LazyULPromo = lazy(() => import("../pages/ULPromo"));
const LazyLpUnlimited = lazy(() => import("../pages/Home"));
const LazyCoverageMap = lazy(() => import("../pages/CoverageMap"));
const LazyPaymentLanding = lazy(() => import("../pages/PaymentLanding"));
const LazyPlanLandingPage = lazy(() => import("../pages/PlanLandingPage"))

const AppRoutes: React.FC = () => {
  const { seoData } = useAppSelector(
    (state: any) => state?.strapi?.commonPage || {}
  );
  const { isSubDomainLoading, subDomainData } = useAppSelector(
    (state: any) => state.homepage
  );

  const pathname = landingPageInfo()?.route || window.location.pathname;
  const landingPageEndPoint =
  pathname === RoutingURLs.lpAllPlans
    ? RoutingURLs.lpAllPlans
    : pathname === RoutingURLs.lpByTheGig
    ? RoutingURLs.lpByTheGig
    : pathname === RoutingURLs.lpEssential
    ? RoutingURLs.lpEssential
    : pathname === RoutingURLs.lpUnlimited
    ? RoutingURLs.lpUnlimited
    : RoutingURLs.lpUnlimitedPlus;

    const loader = () => {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="70vh" // Full viewport height to center vertically
        >
          <Box component="img" alt="circular_loader" src={circularLoaderReach} />
        </Box>
      );
    };
    
  return (
    <>
      <BrowserRouter>
        {isSubDomainLoading ? (
          <Box
            sx={{
              height: "100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              alt={"circular_loader"}
              src={circularLoaderReach}
            />
          </Box>
        ) : (
          <>
            <Header />
            <ReactNotifications />
            <ScrollToTop />

            <div className="mainRoutes">
              <HelmetCust helmetPageName={"home"} />
              <Routes>
                <Route
                  path="*"
                  element={<Navigate to={RoutingURLs.default} replace />}
                />
                <Route
                  path={RoutingURLs.default}
                  element={
                    <Suspense>
                      <LazyHome />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.home}
                  element={
                    <Suspense>
                      <LazyHome />
                    </Suspense>
                  }
                />
                <Route
                  path={landingPageEndPoint}
                  element={
                    <Suspense>
                      <LazyPlanLandingPage />
                    </Suspense>
                  }
                />
                <Route
                  path={EnvConfig.UNLIMITED_LANDING_URL1}
                  element={
                    <Suspense>
                      <LazyLpUnlimited />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.aboutUs}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyAboutUs />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.internationalServices}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyInternationalServices />
                    </Suspense>
                  }
                />
                {/* Prelogin routes */}
                <Route
                  path={RoutingURLs.plans}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyHome />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.cellPhonePlans}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyPlans />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.bestfit}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyBestFit />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.reviews}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyReviews />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.imeiCheck}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyImeiCompatibilityCheck />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.terms}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyTerms />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.warrantyReturnsPolicy}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyWarranty />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.unlimitedBundleTerms}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyULPromoTerms />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.privacy}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyPrivacy />
                    </Suspense>
                  }
                />

                <Route
                  path={RoutingURLs.giveBack}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyGiveBack />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.login}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyLogin />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.checkout}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyCheckout />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.signUp}
                  element={
                    <Suspense fallback={loader()}>
                      <LazySignUp />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.contactUs}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyContactUs />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.coverageCheck}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyCoverageCheck />
                    </Suspense>
                  }
                />
                {EnvConfig.IS_COVERAGE_MAP_PAGE_ENABLED && (
                  <Route
                    path={RoutingURLs.coverageMap}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyCoverageMap />
                      </Suspense>
                    }
                  />
                )}
                <Route
                  path={RoutingURLs.billCalculator}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyBillCalculator />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.help}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyHelp />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.paymentLanding}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyPaymentLanding />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.paymentSuccess}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyPaymentLanding />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.helpCenter}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyHelpCenter />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.discover}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyDiscover />
                    </Suspense>
                  }
                />
                <Route
                  path={RoutingURLs.ulPromo}
                  element={
                    <Suspense fallback={loader()}>
                      <LazyULPromo />
                    </Suspense>
                  }
                />
                <Route element={<ProtectedRoutes />}>
                  <Route
                    path={RoutingURLs.profile}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyProfile />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.postActivationHome}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyPostHome />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.myAccount}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyMyAccount />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.trackOrder}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyTrackOrder />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.deviceConfirmCheck}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyDeviceConfirmCheck />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.activateEsim}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyActivateEsim />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.secondarylogin}
                    element={
                      <Suspense fallback={loader()}>
                        <LazySecondaryUserHome />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.steps}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyActivationSteps />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.activation}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyActivation />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.account}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyAccount />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.billing}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyBilling />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.services}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyServices />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.settings}
                    element={
                      <Suspense fallback={loader()}>
                        <LazySettings />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.loader}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyLoaderPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path={RoutingURLs.wallet}
                    element={
                      <Suspense fallback={loader()}>
                        <LazyWallet />
                      </Suspense>
                    }
                  />
                </Route>
                {/* <Route
              path="/byod"
              element={<Navigate to="/imei-compatibility-check" replace />}
            />
            <Route
              path="/chat"
              element={<Navigate to="/#hs-chat-open" replace />}
            />
          {/* 
            <Route
              path="/recover"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyRecoverPassword />{" "}
                </Suspense>
              }
            />
            <Route
              path="/settings"
              element={
                <Suspense fallback={loader()}>
                  <LazySettings />
                </Suspense>
              }
            />
            <Route
              path="/basic-plan"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingNew history={" "} />{" "}
                </Suspense>
              }
            />
            <Route
              path="/moderate-plan"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingNew history={" "} />{" "}
                </Suspense>
              }
            />
            <Route
              path="/people-choice-plan"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingNew history={" "} />{" "}
                </Suspense>
              }
            />
            <Route
              path="/all-in-plan"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingNew history={" "} />{" "}
                </Suspense>
              }
            />
            <Route
              path="/lp01-allplans"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingNew history={" "} />{" "}
                </Suspense>
              }
            />
            <Route
              path="/lp01-giveback"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingGiveBack history={" "} />{" "}
                </Suspense>
              }
            />
            <Route
              path="/lp01-individual"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingIndividual history={" "} />{" "}
                </Suspense>
              }
            /> */}
                {/* <Route
              path="/lp01-bestfit"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingBestFit history={" "} />{" "}
                </Suspense>
              }
            /> */}
                {/* <Route
              path="/lp01-crossout"
              element={
                <Suspense fallback={loader()}>
                  {" "}
                  <LazyLandingCrossFit history={" "} />{" "}
                </Suspense>
              }
            /> */}
              </Routes>
            </div>
            <Footer />
          </>
        )}
      </BrowserRouter>
    </>
  );
};
export default AppRoutes;
